import { useEffect } from 'react'
import { SITE_ORIGIN } from '../config/marketing'

/**
 * Per-route document head for the public marketing pages.
 *
 * StudioFlow ships as a client-rendered SPA — there is no SSR and no prerender
 * step today, so index.html carries ONE static title/description for every
 * route and indexability rests entirely on Googlebot executing the bundle.
 * This hook does not change that; it makes each marketing route describe itself
 * correctly once rendered (title, description, canonical, OG, hreflang, JSON-LD),
 * which is what a rendering crawler reads and what a link preview shows.
 *
 * See docs/architecture/marketing-surfaces.md for the prerender follow-up: the
 * comparison pages want real static HTML before they can be expected to rank.
 *
 * Every tag this hook writes is stamped `data-sf-seo` so unmount can remove
 * exactly what it added and nothing else — navigating from /press back into the
 * app must not leave a press canonical behind on a dashboard URL.
 */

const MARK = 'data-sf-seo'

/**
 * Set on <html> once this hook has finished writing the head.
 *
 * `scripts/prerender-marketing.mjs` waits for exactly this selector before
 * capturing the document — not `networkidle`, which says nothing about whether
 * the head has been rewritten and which a page with a websocket or a polling
 * query never reaches.
 *
 * It lives in useSeo rather than in MarketingShell because React runs effects
 * bottom-up: MarketingShell is a CHILD of the page component, so a marker set
 * there would fire BEFORE the page's own useSeo effect and the prerenderer
 * would capture index.html's placeholder title.
 */
const READY_MARK = 'data-prerendered'

export interface HreflangAlternate {
  /** BCP-47 tag or "x-default". */
  hreflang: string
  /** Absolute URL or site-root-relative path. */
  href: string
}

export interface SeoOptions {
  title: string
  description: string
  /** Site-root-relative path (e.g. `/press`) or an absolute URL. */
  canonicalPath: string
  /** Written to <html lang>. Restored on unmount. */
  lang?: string
  ogType?: string
  /** Site-root-relative path or absolute URL of the OG image. */
  ogImage?: string
  /** hreflang pairs — emit only when the page genuinely exists in both languages. */
  alternates?: HreflangAlternate[]
  /** schema.org blocks, injected as <script type="application/ld+json">. */
  jsonLd?: unknown[]
}

/** Site-root-relative → absolute, absolute passes through unchanged. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  return `${SITE_ORIGIN}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  // Reuse index.html's own tag when there is one (description) so the page
  // description is REPLACED rather than duplicated — two <meta name=description>
  // is a crawler coin-flip. Ours is marked so unmount can restore the original.
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    el.setAttribute(MARK, 'added')
    document.head.appendChild(el)
  } else if (!el.hasAttribute(MARK)) {
    el.setAttribute(MARK, el.getAttribute('content') ?? '')
  }
  el.setAttribute('content', content)
}

function restoreMeta(attr: 'name' | 'property', key: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"][${MARK}]`)
  if (!el) return
  const original = el.getAttribute(MARK)
  if (original === 'added') {
    el.remove()
  } else {
    el.setAttribute('content', original ?? '')
    el.removeAttribute(MARK)
  }
}

export function useSeo(options: SeoOptions) {
  const {
    title,
    description,
    canonicalPath,
    lang,
    ogType = 'website',
    ogImage,
    alternates,
    jsonLd,
  } = options

  // `alternates` and `jsonLd` are fresh array/object literals on every render in
  // practice, so they are serialised into the dependency list rather than
  // compared by identity — otherwise the effect re-runs on every render and
  // rewrites the whole head each time.
  const alternatesKey = JSON.stringify(alternates ?? null)
  const jsonLdKey = JSON.stringify(jsonLd ?? null)

  useEffect(() => {
    const previousTitle = document.title
    const previousLang = document.documentElement.lang
    const url = absoluteUrl(canonicalPath)

    document.title = title
    if (lang) document.documentElement.lang = lang

    setMeta('name', 'description', description)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', url)
    setMeta('property', 'og:type', ogType)
    setMeta('property', 'og:site_name', 'StudioFlow')
    setMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)
    if (ogImage) {
      setMeta('property', 'og:image', absoluteUrl(ogImage))
      setMeta('name', 'twitter:image', absoluteUrl(ogImage))
    }

    const canonical = document.createElement('link')
    canonical.rel = 'canonical'
    canonical.href = url
    canonical.setAttribute(MARK, 'added')
    document.head.appendChild(canonical)

    const alternateLinks = (alternates ?? []).map((alt) => {
      const link = document.createElement('link')
      link.rel = 'alternate'
      link.hreflang = alt.hreflang
      link.href = absoluteUrl(alt.href)
      link.setAttribute(MARK, 'added')
      document.head.appendChild(link)
      return link
    })

    // textContent, never innerHTML: the payload is JSON built from our own
    // content modules, but the habit is what keeps a future page that
    // interpolates a URL param out of trouble.
    const jsonLdScripts = (jsonLd ?? []).map((block) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(block)
      script.setAttribute(MARK, 'added')
      document.head.appendChild(script)
      return script
    })

    // Last statement in the effect, deliberately: everything the prerenderer is
    // told to wait for has been appended by the time this attribute appears.
    document.documentElement.setAttribute(READY_MARK, 'ready')

    return () => {
      document.title = previousTitle
      if (lang) document.documentElement.lang = previousLang
      restoreMeta('name', 'description')
      restoreMeta('property', 'og:title')
      restoreMeta('property', 'og:description')
      restoreMeta('property', 'og:url')
      restoreMeta('property', 'og:type')
      restoreMeta('property', 'og:site_name')
      restoreMeta('name', 'twitter:card')
      restoreMeta('name', 'twitter:title')
      restoreMeta('name', 'twitter:description')
      restoreMeta('property', 'og:image')
      restoreMeta('name', 'twitter:image')
      canonical.remove()
      alternateLinks.forEach((l) => l.remove())
      jsonLdScripts.forEach((s) => s.remove())
      // Navigating away from a marketing route leaves a head that is no longer
      // described, so the marker goes with it — otherwise a capture of a route
      // reached by client-side navigation would look ready while it isn't.
      document.documentElement.removeAttribute(READY_MARK)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalPath, lang, ogType, ogImage, alternatesKey, jsonLdKey])
}
